import SectionConfiguration from './SectionConfiguration';
import {FieldId, SectionId} from './types';
import FieldConfiguration from './FieldConfiguration';
import {SectionSkeleton} from './SectionSkeleton';
import {getSectionConfiguration} from '../../components/formConfiguration/Sections';

export default class ConfigurationList {
  constructor(readonly configurations: SectionConfiguration[], readonly skeletons: SectionSkeleton[]) {
  }

  delete(id: SectionId): ConfigurationList {
    return new ConfigurationList(this.configurations.filter((x) => x.id !== id), this.skeletons);
  }

  replace(id: SectionId, configuration: SectionConfiguration): ConfigurationList {
    return new ConfigurationList(this.replaceConfiguration(id, configuration), this.skeletons);
  }

  append(configuration: SectionConfiguration): ConfigurationList {
    return new ConfigurationList(
      this.configurations.concat([configuration]),
      this.skeletons.concat([configuration.toSkeleton()]),
    );
  }

  appendField(fieldConfiguration: FieldConfiguration): ConfigurationList {
    const configuration = this.configurations.find((x) => x.id === fieldConfiguration.field.sectionId);
    if (configuration) {
      const newConfiguration = configuration.replaceFields(configuration.fields.concat([fieldConfiguration]));
      return new ConfigurationList(
        this.replaceConfiguration(fieldConfiguration.field.sectionId, newConfiguration),
        this.skeletons,
      );
    }
    return new ConfigurationList(this.configurations, this.skeletons);
  }

  replaceField(fieldConfiguration: FieldConfiguration): ConfigurationList {
    const configuration = this.findByField(fieldConfiguration.id);
    const newConfiguration = getSectionConfiguration(fieldConfiguration.field.sectionId, this.configurations);
    if (configuration) {
      if (configuration.id === fieldConfiguration.field.sectionId) {
        const index = configuration.fields.findIndex((x) => x.id === fieldConfiguration.id);
        const newFields = ConfigurationList.replaceInIndex(index, fieldConfiguration, configuration.fields);
        return this.replace(configuration.id, configuration.replaceFields(newFields));
      } else if (newConfiguration) {
        return this
          .replace(configuration.id, configuration.removeFields([fieldConfiguration.id]))
          .replace(newConfiguration.id, newConfiguration.replaceFields(newConfiguration.fields.concat([fieldConfiguration])));
      }
    }
    return this;
  }

  protected replaceConfiguration(id: SectionId, configuration: SectionConfiguration): SectionConfiguration[] {
    const index = this.configurations.findIndex((x) => x.id === configuration.id);
    return ConfigurationList.replaceInIndex(index, configuration, this.configurations);
  }

  protected findByField(fieldId: FieldId): SectionConfiguration | undefined {
    return this.configurations.find((x) => x.fields.find((y) => y.id === fieldId) !== undefined);
  }

  protected static replaceInIndex<T>(index: number, el: T, elements: T[]): T[] {
    return elements.slice(0, index).concat([el]).concat(elements.slice(index + 1));
  }
}

